import React from 'react';

/**
 * BrandCanvas — hi-fi, brand-inspired UI mockup imagery for named case studies.
 * All artwork is our original design, styled to evoke the client's actual
 * digital surface. No client screenshots are reproduced verbatim.
 *
 * Each canvas is composed of an SVG holding a phone frame, a desktop frame,
 * or both — giving the case study page a real "product proof" visual band
 * rather than an abstract mockup.
 */

const INK = '#1A1A1A';
const CREAM = '#FBFAF6';
const SUNK = '#F4F2EC';
const RULE = '#E8E4D9';

// ---------------------------------------------------------------------------
// Sephora — beauty commerce (black on cream, editorial serif, product-led)
// ---------------------------------------------------------------------------
function SephoraCanvas() {
  return (
    <svg viewBox="0 0 900 480" className="w-full h-full" role="img" aria-label="Sephora beauty commerce — reference mockup">
      <rect x="0" y="0" width="900" height="480" fill={SUNK} />
      {/* Desktop frame */}
      <g transform="translate(40 40)">
        <rect x="0" y="0" width="560" height="400" rx="14" fill={INK} />
        <rect x="6" y="6" width="548" height="388" rx="10" fill={CREAM} />
        {/* Top nav */}
        <rect x="20" y="22" width="90" height="10" rx="2" fill={INK} />
        <g transform="translate(140 24)">
          {['NEW', 'BRANDS', 'MAKEUP', 'SKINCARE', 'FRAGRANCE', 'GIFTS'].map((label, i) => (
            <text key={i} x={i * 62} y="8" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} letterSpacing="1.5">
              {label}
            </text>
          ))}
        </g>
        <g transform="translate(516 22)">
          <circle cx="8" cy="8" r="6" fill="none" stroke={INK} strokeWidth="1.2" />
          <line x1="12" y1="12" x2="18" y2="18" stroke={INK} strokeWidth="1.2" />
        </g>
        <line x1="20" y1="44" x2="540" y2="44" stroke={RULE} />

        {/* PDP hero */}
        <g transform="translate(20 60)">
          {/* Product silhouette (lipstick + fragrance bottle) */}
          <rect x="0" y="0" width="220" height="280" rx="6" fill="#F0E7DE" />
          {/* Lipstick */}
          <g transform="translate(60 40)">
            <rect x="0" y="0" width="24" height="90" rx="3" fill="#3B2320" />
            <rect x="4" y="12" width="16" height="24" rx="2" fill="#B33A3A" />
          </g>
          {/* Fragrance bottle */}
          <g transform="translate(112 60)">
            <rect x="0" y="0" width="70" height="130" rx="6" fill="#F4C7B5" />
            <rect x="24" y="-14" width="22" height="18" rx="2" fill="#3B2320" />
            <rect x="12" y="34" width="46" height="14" fill={INK} opacity="0.85" />
            <text x="35" y="45" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="8" fill={CREAM} letterSpacing="2">EAU DE PARFUM</text>
          </g>
          {/* Thumbs */}
          <g transform="translate(14 236)">
            {[0,1,2,3].map((i) => (
              <rect key={i} x={i*46} y="0" width="40" height="32" rx="4" fill="#E1D5C9" stroke={i===0?INK:'transparent'} />
            ))}
          </g>
        </g>

        {/* Right column — details */}
        <g transform="translate(260 60)">
          <text x="0" y="10" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={INK} opacity="0.55" letterSpacing="3">FRAGRANCE · EAU DE PARFUM</text>
          <text x="0" y="46" fontFamily="Fraunces, serif" fontSize="30" fontWeight="500" fill={INK}>Nuit d’Amber</text>
          <text x="0" y="72" fontFamily="Inter, sans-serif" fontSize="12" fill={INK} opacity="0.7">MAISON · 50ml</text>
          <text x="0" y="102" fontFamily="Fraunces, serif" fontSize="20" fill={INK}>AED 640</text>
          {/* size chips */}
          <g transform="translate(0 120)">
            {['30 ML', '50 ML', '100 ML'].map((s, i) => (
              <g key={i} transform={`translate(${i*72} 0)`}>
                <rect width="60" height="26" rx="13" fill={i===1?INK:CREAM} stroke={INK} />
                <text x="30" y="17" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={i===1?CREAM:INK}>{s}</text>
              </g>
            ))}
          </g>
          {/* Buttons */}
          <rect x="0" y="164" width="240" height="36" rx="4" fill={INK} />
          <text x="120" y="187" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="11" fill={CREAM} letterSpacing="3">ADD TO BAG</text>
          <rect x="0" y="210" width="240" height="34" rx="4" fill="none" stroke={INK} />
          <text x="120" y="232" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} letterSpacing="3">FREE STORE PICK-UP · UAE</text>
          {/* Beauty Pass */}
          <rect x="0" y="256" width="240" height="60" rx="6" fill="#F0E7DE" />
          <text x="14" y="278" fontFamily="Fraunces, serif" fontSize="13" fill={INK}>Beauty Pass · Gold</text>
          <text x="14" y="296" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} opacity="0.7">Earn 128 points on this order</text>
          <circle cx="220" cy="288" r="12" fill={INK} />
          <text x="220" y="292" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="10" fill={CREAM}>G</text>
        </g>
      </g>

      {/* Mobile frame (right) */}
      <g transform="translate(636 40)">
        <rect x="0" y="0" width="220" height="400" rx="30" fill={INK} />
        <rect x="8" y="12" width="204" height="376" rx="22" fill={CREAM} />
        {/* Status bar */}
        <text x="26" y="30" fontFamily="Inter, sans-serif" fontSize="9" fill={INK}>9:41</text>
        {/* Hero card */}
        <g transform="translate(20 44)">
          <rect x="0" y="0" width="180" height="120" rx="8" fill="#E1D5C9" />
          <rect x="12" y="12" width="60" height="9" rx="2" fill={INK} />
          <text x="12" y="42" fontFamily="Fraunces, serif" fontSize="18" fill={INK}>Ramadan</text>
          <text x="12" y="62" fontFamily="Fraunces, serif" fontSize="18" fill={INK} fontStyle="italic">edit.</text>
          <rect x="12" y="80" width="80" height="24" rx="3" fill={INK} />
          <text x="52" y="96" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} letterSpacing="2">SHOP</text>
          {/* corner glyph */}
          <path d="M148 12 L168 12 L168 32 Z" fill={INK} opacity="0.85" />
        </g>
        {/* Product grid */}
        <g transform="translate(20 180)">
          <text fontFamily="JetBrains Mono, monospace" fontSize="8" fill={INK} opacity="0.55" letterSpacing="2">FOR YOU</text>
          {[0,1,2,3].map((i) => (
            <g key={i} transform={`translate(${(i%2)*94} ${16 + Math.floor(i/2)*100})`}>
              <rect x="0" y="0" width="84" height="84" rx="6" fill="#F0E7DE" />
              <g transform="translate(24 18)">
                <rect x="0" y="0" width="16" height="48" rx="2" fill={i%2 ? '#B33A3A' : '#3B2320'} />
                <rect x="30" y="8" width="12" height="34" rx="2" fill={i%2 ? '#F4C7B5' : '#E1D5C9'} />
              </g>
              <text x="4" y="98" fontFamily="Inter, sans-serif" fontSize="8" fill={INK}>Fragrance</text>
            </g>
          ))}
        </g>
        {/* Bottom nav */}
        <line x1="18" y1="352" x2="202" y2="352" stroke={RULE} />
        <g transform="translate(24 366)">
          {['Home', 'Shop', 'Scan', 'Bag', 'Me'].map((t, i) => (
            <text key={i} x={i*38} y="0" fontFamily="Inter, sans-serif" fontSize="8" fill={i===0?INK:INK} opacity={i===0?1:0.5}>{t}</text>
          ))}
        </g>
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Blue SuperApp — bright cyan-blue, rewards / wallet / cross-brand grid.
// Referenced from the client's attached Blue Rewards / Blue Wallet screens.
// ---------------------------------------------------------------------------
function BlueCanvas() {
  const BLUE = '#00A8E1';
  const BLUE_DEEP = '#003E7E';
  const BLUE_DARK = '#001B45';
  return (
    <svg viewBox="0 0 900 480" className="w-full h-full" role="img" aria-label="Blue SuperApp — reference mockup">
      <defs>
        <linearGradient id="bluebg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={BLUE} />
          <stop offset="1" stopColor={BLUE_DARK} />
        </linearGradient>
        <linearGradient id="wallet" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FF3D8A" />
          <stop offset="1" stopColor="#FFB84C" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="900" height="480" fill="url(#bluebg)" />

      {/* Decorative swoosh */}
      <path d="M-10 340 Q 240 220 500 320 T 920 260" stroke={BLUE} strokeOpacity="0.28" strokeWidth="60" fill="none" strokeLinecap="round" />

      {/* Phone 1 — Wallet */}
      <g transform="translate(80 40)">
        <rect x="0" y="0" width="220" height="400" rx="30" fill={INK} />
        <rect x="8" y="12" width="204" height="376" rx="22" fill={BLUE} />
        <text x="26" y="34" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM}>9:41</text>
        <text x="26" y="76" fontFamily="Inter, sans-serif" fontSize="20" fill={CREAM} fontWeight="700">Wallet</text>

        {/* Balance card */}
        <g transform="translate(20 96)">
          <rect x="0" y="0" width="180" height="70" rx="10" fill="url(#wallet)" />
          <text x="14" y="26" fontFamily="Inter, sans-serif" fontSize="13" fill={CREAM} fontWeight="700">AED 10,542.98</text>
          <text x="14" y="42" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM} letterSpacing="2">TOTAL BALANCE</text>
          <rect x="0" y="52" width="180" height="18" rx="0" fill={CREAM} />
          <text x="90" y="64" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={INK}>Show to Earn</text>
        </g>

        {/* FAB Credit card */}
        <g transform="translate(20 180)">
          <rect x="0" y="0" width="180" height="52" rx="8" fill={BLUE_DEEP} />
          <rect x="10" y="10" width="30" height="20" rx="2" fill={BLUE} />
          <text x="20" y="24" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="12" fill={CREAM} fontWeight="500">B</text>
          <text x="48" y="20" fontFamily="Inter, sans-serif" fontSize="7" fill={CREAM} opacity="0.7">World of Benefits</text>
          <text x="48" y="32" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} fontWeight="600">Blue FAB Credit Card</text>
        </g>

        {/* Actions */}
        <g transform="translate(20 246)">
          <rect x="0" y="0" width="180" height="30" rx="15" fill={CREAM} />
          <text x="90" y="20" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} fontWeight="600">Blue Pay</text>

          {['Top-up','Transfer','Pay Bills','Donate'].map((a, i) => (
            <g key={a} transform={`translate(${i*44} 44)`}>
              <circle cx="20" cy="14" r="14" fill={CREAM} opacity="0.16" />
              <text x="20" y="42" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM}>{a}</text>
            </g>
          ))}
        </g>

        {/* Bottom nav */}
        <g transform="translate(20 342)">
          {['Home','Deals','Wallet','My Blue','Profile'].map((t,i) => (
            <text key={t} x={i*36} y="24" fontFamily="Inter, sans-serif" fontSize="7" fill={CREAM} opacity={i===2?1:0.6}>{t}</text>
          ))}
        </g>
      </g>

      {/* Phone 2 — Cross-brand offers */}
      <g transform="translate(340 40)">
        <rect x="0" y="0" width="220" height="400" rx="30" fill={INK} />
        <rect x="8" y="12" width="204" height="376" rx="22" fill={BLUE} />
        <text x="26" y="34" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM}>9:41</text>
        <text x="20" y="70" fontFamily="Inter, sans-serif" fontSize="14" fill={CREAM} fontWeight="700">OFFERS FROM TOP BRANDS</text>

        {/* Category tiles */}
        <g transform="translate(20 90)">
          {[
            { l:'Shop', c:'#4A90E2' },
            { l:'Dine', c:'#FFB84C' },
            { l:'Play', c:'#8A6DFF' },
            { l:'Scan & Earn', c:'#FF3D8A' },
            { l:'My Vouchers', c:'#5AC4FF' },
            { l:'Use My Points', c:'#FFD066' },
            { l:'Mall Gift Card', c:'#FF7A6C' },
            { l:'Navigate In-Mall', c:'#00D6A2' },
          ].map((tile, i) => (
            <g key={tile.l} transform={`translate(${(i%4)*44} ${Math.floor(i/4)*54})`}>
              <rect x="0" y="0" width="36" height="36" rx="8" fill={tile.c} />
              <text x="18" y="52" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="6" fill={CREAM}>{tile.l}</text>
            </g>
          ))}
        </g>

        {/* Explore stores */}
        <g transform="translate(20 220)">
          <text fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} fontWeight="700">Explore stores</text>
          {[
            { n:'IKEA',   o:'4 offers · 6 loc', c:'#FFC907' },
            { n:'M&S',    o:'4 offers · 6 loc', c:'#101820' },
            { n:'Café B', o:'4 offers',          c:'#E1D5C9' },
            { n:'Watsons',o:'4 offers',          c:'#00A96E' },
          ].map((s, i) => (
            <g key={s.n} transform={`translate(${(i%2)*90} ${16 + Math.floor(i/2)*36})`}>
              <rect x="0" y="0" width="82" height="30" rx="6" fill={BLUE_DEEP} />
              <rect x="4" y="4" width="22" height="22" rx="4" fill={s.c} />
              <text x="30" y="14" fontFamily="Inter, sans-serif" fontSize="7" fill={CREAM} fontWeight="600">{s.n}</text>
              <text x="30" y="24" fontFamily="Inter, sans-serif" fontSize="6" fill={CREAM} opacity="0.7">{s.o}</text>
            </g>
          ))}
        </g>
      </g>

      {/* Side panel — outcomes (foreignObject → HTML so long copy wraps cleanly) */}
      <foreignObject x="600" y="40" width="260" height="400">
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          height: '100%',
          padding: '20px',
          borderRadius: '14px',
          background: 'rgba(251,250,246,0.08)',
          border: '1px solid rgba(251,250,246,0.22)',
          boxSizing: 'border-box',
          fontFamily: 'Inter, sans-serif',
          color: CREAM,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '9px', letterSpacing: '2px', opacity: 0.8 }}>
            ONE APP · MANY BRANDS
          </div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: '22px', fontWeight: 500, lineHeight: 1.15 }}>
            Cross-brand loyalty, unified wallet.
          </div>
          <div style={{ height: '1px', background: 'rgba(251,250,246,0.28)', margin: '4px 0' }} />
          <div style={{ fontSize: '11px', lineHeight: 1.55, opacity: 0.9 }}>
            Group-wide identity, so a customer signs in once and is recognised across every Al-Futtaim business.
          </div>
          <div style={{ fontSize: '11px', lineHeight: 1.55, opacity: 0.9 }}>
            Cross-brand points that used to sit in silos are now a single spendable balance.
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ padding: '10px 12px', borderRadius: '8px', background: 'rgba(251,250,246,0.14)' }}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '18px', fontWeight: 500 }}>200+</div>
              <div style={{ fontSize: '9px', opacity: 0.8, lineHeight: 1.4 }}>Group businesses visible to a single customer profile</div>
            </div>
            <div style={{ padding: '10px 12px', borderRadius: '8px', background: 'rgba(251,250,246,0.14)' }}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '18px', fontWeight: 500 }}>1</div>
              <div style={{ fontSize: '9px', opacity: 0.8, lineHeight: 1.4 }}>Loyalty ledger behind every brand touchpoint</div>
            </div>
          </div>
        </div>
      </foreignObject>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MAGRABi — omnichannel eye-care: PDP (commerce) + in-store terminal
// ---------------------------------------------------------------------------
function MagrabiCanvas() {
  const AMBER = '#B67B3E';
  const NAVY = '#0D2438';
  return (
    <svg viewBox="0 0 900 480" className="w-full h-full" role="img" aria-label="MAGRABi omnichannel eye-care — reference mockup">
      <rect x="0" y="0" width="900" height="480" fill={CREAM} />

      {/* Desktop PDP */}
      <g transform="translate(40 40)">
        <rect x="0" y="0" width="540" height="400" rx="14" fill={INK} />
        <rect x="6" y="6" width="528" height="388" rx="10" fill={CREAM} />

        {/* Top nav */}
        <g transform="translate(20 22)">
          <text x="0" y="12" fontFamily="Fraunces, serif" fontSize="18" fill={INK} fontWeight="600">MAGRABi</text>
          <line x1="70" y1="10" x2="76" y2="10" stroke={INK} strokeWidth="2" />
          {['SUNGLASSES','EYEGLASSES','LENSES','BRANDS','BOOK AN EYE TEST'].map((l,i) => (
            <text key={l} x={110 + i*80} y="12" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} letterSpacing="1.5">{l}</text>
          ))}
        </g>
        <line x1="20" y1="46" x2="518" y2="46" stroke={RULE} />

        {/* Frame preview */}
        <g transform="translate(20 60)">
          <rect x="0" y="0" width="260" height="280" rx="6" fill={SUNK} />
          <g transform="translate(24 120)">
            <ellipse cx="50" cy="0" rx="42" ry="30" fill="none" stroke={NAVY} strokeWidth="4" />
            <ellipse cx="164" cy="0" rx="42" ry="30" fill="none" stroke={NAVY} strokeWidth="4" />
            <path d="M92 0 L122 0" stroke={NAVY} strokeWidth="4" />
            <path d="M8 0 L-8 -14" stroke={NAVY} strokeWidth="4" strokeLinecap="round" />
            <path d="M206 0 L222 -14" stroke={NAVY} strokeWidth="4" strokeLinecap="round" />
          </g>
          {/* Thumbs */}
          <g transform="translate(20 234)">
            {[NAVY, AMBER, '#3B2A22', '#8A8177'].map((c, i) => (
              <g key={i} transform={`translate(${i*54} 0)`}>
                <rect width="44" height="30" rx="4" fill={CREAM} stroke={i===0?INK:RULE} />
                <ellipse cx="14" cy="16" rx="9" ry="6" fill="none" stroke={c} strokeWidth="2" />
                <ellipse cx="30" cy="16" rx="9" ry="6" fill="none" stroke={c} strokeWidth="2" />
              </g>
            ))}
          </g>
        </g>

        {/* Right — details (generic MAGRABi own-brand frame, not a licensed brand) */}
        <g transform="translate(304 60)">
          <text x="0" y="10" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={AMBER} letterSpacing="3">MAGRABi · SIGNATURE</text>
          <text x="0" y="42" fontFamily="Fraunces, serif" fontSize="26" fontWeight="500" fill={INK}>Auric · Classic</text>
          <text x="0" y="60" fontFamily="Inter, sans-serif" fontSize="11" fill={INK} opacity="0.65">Unisex · Full rim · Acetate</text>
          <text x="0" y="90" fontFamily="Fraunces, serif" fontSize="18" fill={INK}>AED 780</text>

          <text x="0" y="120" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} fontWeight="600" letterSpacing="1.5">FRAME COLOUR</text>
          <g transform="translate(0 130)">
            {[NAVY, AMBER, '#3B2A22', '#8A8177'].map((c, i) => (
              <circle key={i} cx={12 + i*32} cy="14" r="10" fill={c} stroke={i===0?INK:'none'} strokeWidth="2" />
            ))}
          </g>

          <text x="0" y="180" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} fontWeight="600" letterSpacing="1.5">LENS OPTIONS</text>
          <g transform="translate(0 190)">
            {['Single vision','Progressive','Reading','Non-Rx'].map((l, i) => (
              <g key={l} transform={`translate(0 ${i*22})`}>
                <circle cx="8" cy="8" r="6" fill="none" stroke={INK} />
                {i===0 && <circle cx="8" cy="8" r="3" fill={INK} />}
                <text x="22" y="12" fontFamily="Inter, sans-serif" fontSize="10" fill={INK}>{l}</text>
              </g>
            ))}
          </g>

          {/* CTA */}
          <rect x="0" y="290" width="200" height="34" fill={NAVY} rx="2" />
          <text x="100" y="311" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} letterSpacing="3">START PRESCRIPTION</text>
        </g>
      </g>

      {/* In-store terminal — customer file lookup surface */}
      <g transform="translate(610 40)">
        <rect x="0" y="0" width="250" height="400" rx="14" fill={INK} />
        <rect x="8" y="8" width="234" height="384" rx="10" fill={CREAM} />

        {/* Terminal header */}
        <g transform="translate(20 24)">
          <text fontFamily="JetBrains Mono, monospace" fontSize="9" fill={AMBER} letterSpacing="2">IN-STORE · DUBAI MALL</text>
          <text y="20" fontFamily="Fraunces, serif" fontSize="16" fill={INK} fontWeight="600">Customer file</text>
        </g>
        <line x1="20" y1="66" x2="230" y2="66" stroke={RULE} />

        {/* Customer summary */}
        <g transform="translate(20 82)">
          <circle cx="18" cy="18" r="18" fill={NAVY} />
          <text x="18" y="22" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="14" fill={CREAM} fontWeight="600">A</text>
          <text x="46" y="14" fontFamily="Inter, sans-serif" fontSize="11" fill={INK} fontWeight="600">Ahmed R.</text>
          <text x="46" y="30" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.6">MAGRABi member · since 2021</text>
        </g>

        {/* Prescription card */}
        <g transform="translate(20 136)">
          <rect x="0" y="0" width="210" height="88" rx="8" fill={SUNK} />
          <text x="14" y="20" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={INK} opacity="0.6" letterSpacing="2">LATEST PRESCRIPTION</text>
          <text x="14" y="38" fontFamily="Inter, sans-serif" fontSize="10" fill={INK}>Right eye: −2.25 SPH · −0.50 CYL</text>
          <text x="14" y="54" fontFamily="Inter, sans-serif" fontSize="10" fill={INK}>Left eye:  −1.75 SPH · −0.25 CYL</text>
          <text x="14" y="74" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.6">Captured online · verified in-store</text>
        </g>

        {/* Timeline */}
        <g transform="translate(20 240)">
          <text fontFamily="Inter, sans-serif" fontSize="9" fill={INK} fontWeight="600" letterSpacing="1.5">RECENT ACTIVITY</text>
          {[
            { d: 'Today',       l: 'Fitting complete',           c: NAVY },
            { d: 'Last month',  l: 'Auric · Classic ordered',    c: AMBER },
            { d: '2 months',    l: 'Prescription updated online', c: INK  },
          ].map((r, i) => (
            <g key={i} transform={`translate(0 ${18 + i*28})`}>
              <circle cx="4" cy="10" r="3" fill={r.c} />
              <text x="16" y="8" fontFamily="Inter, sans-serif" fontSize="8" fill={INK} opacity="0.55" letterSpacing="1.5">{r.d.toUpperCase()}</text>
              <text x="16" y="22" fontFamily="Inter, sans-serif" fontSize="10" fill={INK}>{r.l}</text>
            </g>
          ))}
        </g>

        {/* In-store order pickup card */}
        <g transform="translate(20 340)">
          <rect x="0" y="0" width="210" height="46" rx="8" fill={NAVY} />
          <text x="14" y="18" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={AMBER} letterSpacing="2">IN-STORE ORDER</text>
          <text x="14" y="34" fontFamily="Fraunces, serif" fontSize="11" fill={CREAM}>Ready for pickup — Auric</text>
        </g>
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Wiiv — super-app shell + conversational concierge (Wiivee)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Wiiv — reference imagery drawn from the actual delivered surface:
// magenta-gradient home with Wiiv Wallet + section grid, and a chat surface
// showing an in-app concierge conversation.
// ---------------------------------------------------------------------------
function WiivCanvas() {
  const PINK_DEEP = '#7B1E5C';
  const PINK = '#C1327A';
  const MAG = '#E14A98';
  const CORAL = '#F27F9E';
  const AGENT_HEAD = '#5D1A55';
  return (
    <svg viewBox="0 0 900 480" className="w-full h-full" role="img" aria-label="Wiiv member-facing platform — reference imagery of the delivered surface">
      <defs>
        <linearGradient id="wiivpage" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F5E7EA" />
          <stop offset="1" stopColor="#EAD8DE" />
        </linearGradient>
        <linearGradient id="walletmag" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={MAG} />
          <stop offset="1" stopColor={PINK} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="900" height="480" fill="url(#wiivpage)" />

      {/* Phone 1 — Wallet home */}
      <g transform="translate(60 40)">
        <rect x="0" y="0" width="230" height="400" rx="30" fill={INK} />
        <rect x="8" y="12" width="214" height="376" rx="22" fill={CREAM} />
        <text x="26" y="34" fontFamily="Inter, sans-serif" fontSize="9" fill={INK}>9:41</text>

        {/* Header bar with logo */}
        <g transform="translate(18 46)">
          <rect x="0" y="0" width="194" height="30" rx="4" fill={CREAM} />
          <text x="0" y="18" fontFamily="Fraunces, serif" fontSize="18" fontWeight="700" fill={PINK_DEEP}>wiiv</text>
          <circle cx="176" cy="14" r="10" fill="#F0DDE4" />
          <text x="176" y="18" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill={PINK_DEEP}>PC</text>
        </g>

        {/* Wallet card */}
        <g transform="translate(18 84)">
          <rect x="0" y="0" width="194" height="100" rx="12" fill="url(#walletmag)" />
          <text x="14" y="22" fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} opacity="0.85">Wallet</text>
          <text x="14" y="46" fontFamily="Fraunces, serif" fontSize="24" fill={CREAM} fontWeight="700">Balance</text>
          <text x="14" y="62" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} opacity="0.85">Points · Vouchers · Rewards</text>
          <rect x="14" y="74" width="118" height="18" rx="9" fill={CREAM} />
          <text x="73" y="87" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill={PINK_DEEP} fontWeight="600">Open wallet</text>
        </g>

        {/* Capability grid — generic modules */}
        <g transform="translate(18 200)">
          <text fontFamily="Inter, sans-serif" fontSize="8" fill={INK} opacity="0.55" letterSpacing="1.5">EXPLORE</text>
          {[
            { l: 'Commerce', c: '#003E7E' },
            { l: 'Health',   c: '#2AA07D' },
            { l: 'Travel',   c: '#F4B142' },
            { l: 'Events',   c: '#8A6DFF' },
            { l: 'Stays',    c: '#5AC4FF' },
            { l: 'Payments', c: PINK },
            { l: 'Rewards',  c: '#FF7A5C' },
            { l: 'Concierge', c: MAG },
            { l: 'More',     c: '#B0AEB6' },
          ].map((tile, i) => (
            <g key={tile.l} transform={`translate(${(i%3)*66} ${16 + Math.floor(i/3)*56})`}>
              <rect x="0" y="0" width="58" height="42" rx="8" fill={tile.c} />
              <text x="29" y="60" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={INK}>{tile.l}</text>
            </g>
          ))}
        </g>

        {/* Bottom nav */}
        <g transform="translate(18 358)">
          {['Home','Rewards','Pay','Assist','Me'].map((t,i) => (
            <text key={t} x={i*40} y="0" fontFamily="Inter, sans-serif" fontSize="7" fill={PINK_DEEP} opacity={i===0?1:0.5}>{t}</text>
          ))}
        </g>
      </g>

      {/* Phone 2 — Concierge conversation */}
      <g transform="translate(330 40)">
        <rect x="0" y="0" width="230" height="400" rx="30" fill={INK} />
        <rect x="8" y="12" width="214" height="376" rx="22" fill={CREAM} />
        <text x="26" y="34" fontFamily="Inter, sans-serif" fontSize="9" fill={INK}>9:41</text>

        {/* Concierge header */}
        <g transform="translate(18 46)">
          <rect x="0" y="0" width="194" height="46" rx="10" fill={AGENT_HEAD} />
          <circle cx="20" cy="24" r="10" fill={MAG} />
          <text x="20" y="27" textAnchor="middle" fontFamily="Fraunces, serif" fontSize="10" fill={CREAM} fontWeight="700">w</text>
          <text x="38" y="20" fontFamily="Fraunces, serif" fontSize="12" fill={CREAM} fontWeight="600">Concierge</text>
          <text x="38" y="34" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM} opacity="0.75">Reordering last week’s groceries</text>
        </g>

        {/* Step chips */}
        <g transform="translate(18 102)">
          {['Commerce', 'Review & Pay', 'Confirmed'].map((s, i) => (
            <g key={s} transform={`translate(${i*66} 0)`}>
              <rect x="0" y="0" width="58" height="20" rx="10" fill={i===0?PINK:CREAM} stroke={i===0?PINK:'#E1CBD3'} strokeWidth="1" />
              <text x="29" y="14" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={i===0?CREAM:INK}>{s}</text>
            </g>
          ))}
        </g>

        {/* Order card */}
        <g transform="translate(18 132)">
          <rect x="0" y="0" width="194" height="130" rx="10" fill={CREAM} stroke="#F1D8E0" />
          <text x="14" y="22" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.65">Order · 22 items</text>
          {[
            { l: 'Bread · Loaf',       q: '×2', p: '90' },
            { l: 'Milk · 1L',          q: '×3', p: '165' },
            { l: 'Eggs · Dozen',       q: '×1', p: '130' },
            { l: 'Chicken · Boneless', q: '×1', p: '420' },
          ].map((r, i) => (
            <g key={i} transform={`translate(14 ${34 + i*20})`}>
              <text fontFamily="Inter, sans-serif" fontSize="9" fill={INK}>{r.l}</text>
              <text x="110" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.55">{r.q}</text>
              <text x="150" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} fontWeight="600">{r.p}</text>
            </g>
          ))}
        </g>

        {/* Total + free delivery */}
        <g transform="translate(18 272)">
          <text fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.6">Order total</text>
          <text x="104" fontFamily="Fraunces, serif" fontSize="14" fill={INK} fontWeight="600">1,240</text>
          <rect x="0" y="10" width="194" height="24" rx="6" fill="#EFFAEE" stroke="#B8E0B0" />
          <text x="97" y="26" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill="#2A6B2A">Free delivery on this order</text>
        </g>

        {/* Compliance nudge */}
        <g transform="translate(18 310)">
          <rect x="0" y="0" width="194" height="46" rx="8" fill="#FFF5E1" stroke="#EBCF7C" />
          <text x="14" y="18" fontFamily="Inter, sans-serif" fontSize="8" fill={INK} opacity="0.75" letterSpacing="1.5">TIER · 30-SEC UPLOAD</text>
          <text x="14" y="34" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} fontWeight="600">Complete your verification</text>
        </g>
      </g>

      {/* Right side panel — architectural note */}
      <foreignObject x="600" y="40" width="260" height="400">
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          height: '100%', padding: '20px', borderRadius: '14px',
          background: '#FBFAF6', border: '1px solid #E8E4D9',
          boxSizing: 'border-box', color: INK,
          fontFamily: 'Inter, sans-serif',
          display: 'flex', flexDirection: 'column', gap: '12px',
        }}>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, letterSpacing: 2, opacity: 0.7, color: PINK_DEEP }}>DELIVERED SURFACE</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 500, lineHeight: 1.15 }}>One shell. One member.</div>
          <div style={{ height: 1, background: '#E8E4D9' }} />
          <div style={{ fontSize: 12, lineHeight: 1.55, opacity: 0.75 }}>
            Reference imagery drawn from the delivered surface — original artwork, not a client screenshot.
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.55, opacity: 0.75 }}>
            The concierge rides on the same downstream APIs the capability modules already use — every partner it needs to reach is already connected.
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#F4F2EC' }}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18, fontWeight: 500 }}>2 wks</div>
              <div style={{ fontSize: 10, opacity: 0.65, lineHeight: 1.4 }}>Architecture direction engagement, end-to-end</div>
            </div>
          </div>
        </div>
      </foreignObject>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// NorthLadder — reference imagery from the delivered surface: dark supplier
// dashboard (demand analytics) + light buyer configurator (colour-mix + WAP).
// ---------------------------------------------------------------------------
function NorthLadderCanvas() {
  const NLNAVY = '#0D2438';
  const NLDARK = '#0A1B2C';
  const NLAMBER = '#B6844B';
  const NLTEAL = '#3B9F97';
  return (
    <svg viewBox="0 0 900 480" className="w-full h-full" role="img" aria-label="NorthLadder marketplace — reference imagery of the delivered surface">
      <rect x="0" y="0" width="900" height="480" fill={SUNK} />

      {/* Supplier — dark theme dashboard (left) */}
      <g transform="translate(30 30)">
        <rect x="0" y="0" width="420" height="420" rx="14" fill={NLDARK} />

        {/* Top bar */}
        <g transform="translate(20 22)">
          <text fontFamily="Fraunces, serif" fontSize="14" fill={CREAM} fontWeight="600">Sourcing Marketplace</text>
          <text x="130" y="0" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={NLAMBER} letterSpacing="2">SUPPLIER · DEMAND ANALYTICS</text>
          <circle cx="378" cy="-4" r="10" fill={NLNAVY} />
          <text x="378" y="0" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM}>SV</text>
        </g>
        <line x1="20" y1="42" x2="400" y2="42" stroke={CREAM} strokeOpacity="0.1" />

        {/* Column layout: table + right panel */}
        <g transform="translate(20 56)">
          <text fontFamily="JetBrains Mono, monospace" fontSize="8" fill={CREAM} opacity="0.55" letterSpacing="2">UNMET REQUESTS · 87</text>
          {[
            { sku: 'iPhone 16 Pro',        q: '55u', p: '€380', st: 'Open' },
            { sku: 'iPhone 16 Pro Max',    q: '32u', p: '€420', st: 'Open' },
            { sku: 'iPhone 15 · A-grade',  q: '48u', p: '€310', st: 'Bid' },
            { sku: 'Galaxy S24 Ultra',    q: '28u', p: '€395', st: 'Open' },
            { sku: 'Pixel 9 Pro',         q: '18u', p: '€280', st: 'Bid' },
          ].map((r, i) => (
            <g key={i} transform={`translate(0 ${20 + i*32})`}>
              <rect x="0" y="0" width="240" height="26" rx="6" fill={i%2===0 ? '#122A44' : NLNAVY} />
              <text x="12" y="17" fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} fontWeight="500">{r.sku}</text>
              <text x="128" y="17" fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} opacity="0.75">{r.q}</text>
              <text x="164" y="17" fontFamily="Inter, sans-serif" fontSize="10" fill={NLAMBER}>{r.p}</text>
              <rect x="196" y="6" width="34" height="14" rx="7" fill={r.st === 'Bid' ? NLTEAL : 'transparent'} stroke={r.st==='Bid'?NLTEAL:CREAM} strokeOpacity={r.st==='Bid'?1:0.4} strokeWidth="0.8" />
              <text x="213" y="16" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM}>{r.st}</text>
            </g>
          ))}
        </g>

        {/* Right side panel — Demand Intelligence */}
        <g transform="translate(276 56)">
          <text fontFamily="JetBrains Mono, monospace" fontSize="8" fill={NLAMBER} letterSpacing="2">DEMAND INTELLIGENCE</text>
          <rect x="0" y="12" width="128" height="90" rx="8" fill={NLNAVY} />
          <text x="12" y="34" fontFamily="Fraunces, serif" fontSize="20" fill={CREAM} fontWeight="600">233u</text>
          <text x="12" y="52" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} opacity="0.75">Unmet Desert Titanium</text>
          <text x="12" y="66" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} opacity="0.75">iPhone 16 Pro demand</text>
          <rect x="12" y="78" width="88" height="14" rx="7" fill={NLAMBER} />
          <text x="56" y="88" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM}>View signal</text>

          <rect x="0" y="112" width="128" height="70" rx="8" fill={NLNAVY} />
          <text x="12" y="132" fontFamily="Fraunces, serif" fontSize="16" fill={CREAM}>100%</text>
          <text x="12" y="150" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} opacity="0.7">Match coverage</text>
          <rect x="12" y="158" width="104" height="4" rx="2" fill={NLTEAL} />
        </g>

        {/* Bottom stat strip */}
        <g transform="translate(20 340)">
          <line x1="0" y1="0" x2="380" y2="0" stroke={CREAM} strokeOpacity="0.1" />
          <g transform="translate(0 24)">
            <text fontFamily="Fraunces, serif" fontSize="18" fill={CREAM}>2,347u</text>
            <text y="18" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM} opacity="0.55">Inventory total</text>
          </g>
          <g transform="translate(140 24)">
            <text fontFamily="Fraunces, serif" fontSize="18" fill={NLAMBER}>€113K</text>
            <text y="18" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM} opacity="0.55">Addressable revenue</text>
          </g>
        </g>
      </g>

      {/* Buyer — light theme configurator (right) */}
      <g transform="translate(470 30)">
        <rect x="0" y="0" width="400" height="420" rx="14" fill={INK} />
        <rect x="6" y="6" width="388" height="408" rx="10" fill={CREAM} />

        <g transform="translate(20 22)">
          <text fontFamily="Fraunces, serif" fontSize="14" fontWeight="600" fill={NLNAVY}>NorthLadder</text>
          <text x="112" y="0" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={INK} opacity="0.55" letterSpacing="2">BUYER · DEVICE MIX CONFIGURATOR</text>
        </g>
        <line x1="20" y1="42" x2="380" y2="42" stroke={RULE} />

        <g transform="translate(20 56)">
          <text fontFamily="Fraunces, serif" fontSize="18" fill={INK} fontWeight="500">iPhone 16 Pro · matched supply</text>
          <text y="18" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} opacity="0.6">Anonymised pool · 3 platform-approved suppliers</text>

          {/* Quantity + colour mix */}
          <g transform="translate(0 38)">
            <rect x="0" y="0" width="80" height="34" rx="4" fill={CREAM} stroke={RULE} />
            <text x="10" y="14" fontFamily="JetBrains Mono, monospace" fontSize="7" fill={INK} opacity="0.55" letterSpacing="2">QTY</text>
            <text x="10" y="28" fontFamily="Fraunces, serif" fontSize="14" fill={INK}>60</text>
            <g transform="translate(96 0)">
              {[
                { c: 'Desert', pill: '#B6844B' },
                { c: 'Natural', pill: '#C7BEB1' },
                { c: 'Black', pill: '#1C2530' },
              ].map((chip, i) => (
                <g key={chip.c} transform={`translate(${i*84} 0)`}>
                  <rect x="0" y="0" width="76" height="34" rx="4" fill={i===0?chip.pill:CREAM} stroke={RULE} />
                  <text x="38" y="21" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={i===0?CREAM:INK}>{chip.c}</text>
                </g>
              ))}
            </g>
          </g>

          {/* Pricing card */}
          <g transform="translate(0 90)">
            <rect x="0" y="0" width="360" height="70" rx="8" fill={NLNAVY} />
            <text x="14" y="20" fontFamily="JetBrains Mono, monospace" fontSize="8" fill={NLAMBER} letterSpacing="2">UNIFIED PRICING · LIVE</text>
            <text x="14" y="46" fontFamily="Fraunces, serif" fontSize="22" fill={CREAM} fontWeight="500">€380.00</text>
            <text x="14" y="62" fontFamily="Inter, sans-serif" fontSize="9" fill={CREAM} opacity="0.7">Fractional allocation across 3 anonymised suppliers</text>
            <rect x="270" y="20" width="76" height="14" rx="7" fill={NLTEAL} />
            <text x="308" y="30" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fill={CREAM} letterSpacing="2">RECOMPUTED</text>
          </g>

          {/* Allocation bar */}
          <g transform="translate(0 178)">
            <text fontFamily="JetBrains Mono, monospace" fontSize="8" fill={INK} opacity="0.55" letterSpacing="2">FRACTIONAL ALLOCATION</text>
            <rect x="0" y="14" width="360" height="14" rx="7" fill={SUNK} />
            <rect x="0" y="14" width="162" height="14" rx="7" fill={NLNAVY} />
            <rect x="162" y="14" width="112" height="14" fill={NLNAVY} opacity="0.75" />
            <rect x="274" y="14" width="86" height="14" rx="7" fill={NLNAVY} opacity="0.55" />
            <g transform="translate(0 36)">
              <text fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.7">Supplier A · 27u  ·  Supplier B · 18u  ·  Supplier C · 15u</text>
              <text y="14" fontFamily="Inter, sans-serif" fontSize="9" fill={INK} opacity="0.5">Buyer never sees supplier identity</text>
            </g>
          </g>

          {/* CTA */}
          <g transform="translate(0 250)">
            <rect x="0" y="0" width="180" height="36" rx="4" fill={NLNAVY} />
            <text x="90" y="22" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={CREAM} letterSpacing="2">CONFIRM ORDER</text>
            <rect x="192" y="0" width="146" height="36" rx="4" fill={CREAM} stroke={NLNAVY} />
            <text x="265" y="22" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill={NLNAVY} letterSpacing="2">SAVE DRAFT</text>
          </g>
        </g>
      </g>
    </svg>
  );
}

const map = {
  wiiv: WiivCanvas,
  northladder: NorthLadderCanvas,
  sephora: SephoraCanvas,
  'al-futtaim': BlueCanvas,
  magrabi: MagrabiCanvas,
};

export default function BrandCanvas({ slug, className = '' }) {
  const Comp = map[slug];
  if (!Comp) return null;
  return (
    <div className={`w-full h-full ${className}`} data-testid={`brand-canvas-${slug}`}>
      <Comp />
    </div>
  );
}

export const hasBrandCanvas = (slug) => Boolean(map[slug]);
